/**
 * M1-7: Scene Info Panel — floating overlay showing scene metadata.
 * Triggered by the circle-info button in the InfoPill (bottom-right).
 *
 * Built on @heroui/react Button + @gravity-ui/icons.
 */
import { useTranslation } from 'react-i18next';
import { useViewerStore } from '@stores/viewerStore';
import { Xmark } from '@gravity-ui/icons';
import { Button } from '@heroui/react';

export function SceneInfoPanel() {
  const { t } = useTranslation();
  const sceneConfig = useViewerStore((s) => s.sceneConfig);
  const isOpen = useViewerStore((s) => s.sceneInfoOpen);
  const setOpen = useViewerStore((s) => s.setSceneInfoOpen);

  if (!isOpen || !sceneConfig) return null;

  return (
    <>
      {/* Backdrop */}
      <button
        type="button"
        className="absolute inset-0 z-40 cursor-default appearance-none border-none bg-transparent p-0"
        aria-label={t('toolbar.close')}
        onClick={() => setOpen(false)}
      />

      {/* Panel */}
      <div className="absolute right-14 bottom-4 z-50 w-80 max-w-[calc(100vw-7rem)] rounded-2xl bg-white/10 p-5 shadow-xl backdrop-blur-md">
        <div className="mb-4 flex items-start justify-between">
          <h3 className="text-base font-semibold text-white">{sceneConfig.name}</h3>
          <Button
            isIconOnly
            size="sm"
            onPress={() => setOpen(false)}
            aria-label={t('toolbar.close')}
            className="ml-2 size-6 min-w-6 shrink-0 rounded-full text-white/40 hover:text-white"
          >
            <Xmark width={14} height={14} />
          </Button>
        </div>

        {sceneConfig.creator && (
          <p className="mb-2 text-xs text-white/50">
            {t('scene.by')} <span className="text-white/70">{sceneConfig.creator.name}</span>
          </p>
        )}

        {sceneConfig.description && (
          <p className="text-sm leading-relaxed text-white/70">{sceneConfig.description}</p>
        )}

        {!sceneConfig.description && (
          <p className="text-sm text-white/30 italic">{t('scene.noDescription')}</p>
        )}
      </div>
    </>
  );
}
